import { ReactNode } from 'react';
import { SelectableTablePossibleCellsType } from '../components/SelectableTable/SelectableTable.types';
import { ExaminerFunction } from '../pages/Assessment/Assessment.types';

export type AnnotationsManagerSortBy = 'annotationType' | 'repeatability' | 'name' | 'description' | 'status';

export enum AnnotationType {
  dynamic,
  static,
}

export interface AnnotationDefinition {
  id: string;
  annotationType: AnnotationType;
  repetitive: boolean;
  name: string;
  description: string;
  active: boolean;
  svg: string;
}

export interface AnnotationDefinitionFilter {
  annotationType: AnnotationType[];
  repetitive: boolean[];
  name: string;
  description: string;
  active: boolean[];
}

export interface AnnotationDefinitionFilterElements {
  annotationType: JSX.Element;
  repetitive: JSX.Element;
  active: JSX.Element;
}

export interface AnnotationDefinitionTableItem {
  [key: string]: SelectableTablePossibleCellsType;
  icon: ReactNode;
  annotationType: string;
  repetitive: ReactNode;
  name: string;
  description: string;
  active: ReactNode;
  actions: ReactNode;
}

export interface AnnotationsForORGZ {
  dtos: AnnotationOnImage[];
}

export interface AnnotationOnImage {
  annotationId: string;
  annotationDefinitionId: string;
  annotationDefinitionVersionId?: string;
  examinerId: string;
  createdByExaminerId?: string;
  isVerifierAnnotation?: boolean;
  examinerFunctionWithZwo?: ExaminerFunction;
  examScoringSchemaId: string;
  forScoring: boolean;
  positionX: number;
  positionY: number;
  pageNumber: number;
  width: number | null;
  height: number | null;
  angle: number | null;
  layer: number | null;
  isVerifier?: boolean;
  isNew?: boolean;
}

export interface AnnotationCriterion {
  number: string;
  annotation: AnnotationDefinitionCriterion;
}

export interface AnnotationDefinitionCriterion {
  active?: boolean;
  annotationDefinitionId?: string;
  annotationType?: AnnotationType;
  description?: string;
  gradingAnnotation?: boolean;
  id: string;
  name: string;
  repetitive?: boolean;
  scoringSchemaAnnotationId?: string;
  sequence?: string;
  svg: string;
  examScoringSchemaId?: string;
}

export interface AnnotationSequence {
  annotationDefinitionId: string;
  gradingAnnotation: boolean;
  scoringSchemaAnnotationId: string;
  sequence: string;
}

export interface AnnotationsCriterionDTO {
  annotationDtos: AnnotationSequence[] | null;
  isSorted: boolean;
}

export interface AnnotationVersion {
  id: string;
  annotationDefinitionId: string;
  icon: string;
  isRepetitive: boolean;
  annotationType: AnnotationType;
}

export type AnnotationVersionsForORGZ = AnnotationVersion[];

export interface AnnotationDefinitionCore {
  annotationDefinitionId: string;
  icon: string;
  isRepetitive: boolean;
  annotationType: AnnotationType;
}

export type CursorDimensions = {
  width: number;
  height: number;
};

export type ImageBorderSettings = {
  hasControls: boolean;
  hasBorders: boolean;
};

export type AnnotationCords = {
  left: number;
  top: number;
};

export const convertAnnotationVersionToAnnotationDefinitionCore = (
  annotationVersion: AnnotationVersion,
): AnnotationDefinitionCore => {
  return {
    annotationDefinitionId: annotationVersion.annotationDefinitionId,
    icon: annotationVersion.icon,
    isRepetitive: annotationVersion.isRepetitive,
    annotationType: annotationVersion.annotationType,
  };
};

export const convertAnnotationDefinitionToAnnotationDefinitionCore = (
  annotationDefinition: AnnotationDefinition,
): AnnotationDefinitionCore => {
  return {
    annotationDefinitionId: annotationDefinition.id,
    icon: annotationDefinition.svg,
    isRepetitive: annotationDefinition.repetitive,
    annotationType: annotationDefinition.annotationType,
  };
};

export type AnnotationsLayers = {
  active: AnnotationOnImage[];
  inactive: AnnotationOnImage[];
};
